/* eslint-disable @typescript-eslint/no-deprecated, max-lines-per-function */
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import type { LocalSectionHomepageDataQuery, LocalSectionHomepageDataQueryVariables } from './api.graphql';
import { LocalSectionHomepageData } from './api.graphql';
import { buildLocationSlug } from './utils';

type LocalSectionLocation = NonNullable<LocalSectionHomepageDataQuery['localSectionLocation']>;
type LocalData = {
	signaturesCount: LocalSectionLocation['totalSignatureCount'];
	signaturesCountLocalized: LocalSectionLocation['totalSignatureCountLocalized'];
	petitionsCount: LocalSectionLocation['totalPetitionCount'];
	petitionsCountLocalized: LocalSectionLocation['totalPetitionCountLocalized'];
	decisionMakersCount: LocalSectionLocation['decisionMakersConnection']['totalCount'];
	decisionMakersCountLocalized: LocalSectionLocation['decisionMakersConnection']['totalCountLocalized'];
};
// doesn't come from the api
type DerivedLocalData = {
	slug: string;
	name: string;
	shortName: string;
	localPageUrl: string;
};

export class LocalSectionDataMissingError extends Error {
	constructor() {
		super('No data returned from local section homepage query');
	}
}

type GetLocalSectionDataReturn = LocalData & DerivedLocalData;
export async function getLocalSectionData(
	location: { city: string | null; stateCode: string | null; countryCode: string | null } | null,
	locale: string,
	{ gql: { fetch }, tracker }: UtilityContext,
): Promise<GetLocalSectionDataReturn> {
	try {
		if (!location) {
			throw new Error('No location provided');
		}

		const { city, stateCode, countryCode } = location;
		if (!city || !stateCode || !countryCode) {
			throw new Error('Invalid location data');
		}

		const slug = buildLocationSlug({ city, stateCode, countryCode });

		const { data } = await fetch<LocalSectionHomepageDataQuery, LocalSectionHomepageDataQueryVariables>({
			query: LocalSectionHomepageData,
			variables: {
				slug,
				locale,
				first: 1,
			},
			rejectOnError: true,
		});

		if (!data.localSectionLocation) {
			throw new Error('No local section location data');
		}

		const {
			totalSignatureCount,
			totalPetitionCount,
			totalSignatureCountLocalized,
			totalPetitionCountLocalized,
			decisionMakersConnection: { totalCount: decisionMakersCount, totalCountLocalized: decisionMakersCountLocalized },
		} = data.localSectionLocation;

		// if the data is a bunch of 0s or undefined we don't want to show the local section
		if (!totalSignatureCount && !totalPetitionCount && !decisionMakersCount) {
			throw new Error('No location data counts');
		}
		const name = `${city}, ${stateCode}`;
		void tracker.track('homepage_local_data_loaded', { user_location: name, slug, has_local_page_data: true });
		return {
			slug,
			name,
			shortName: city,
			localPageUrl: `/local/${slug}`,
			signaturesCount: totalSignatureCount,
			signaturesCountLocalized: totalSignatureCountLocalized,
			petitionsCount: totalPetitionCount,
			petitionsCountLocalized: totalPetitionCountLocalized,
			decisionMakersCount,
			decisionMakersCountLocalized,
		};
	} catch {
		// we don't care what the error type is
		void tracker.track('homepage_local_data_loaded', {
			user_location: undefined,
			slug: undefined,
			has_local_page_data: false,
		});
		throw new LocalSectionDataMissingError();
	}
}

getLocalSectionData.queryKey = 'HOMEPAGE_V2_LOCAL_SECTION_LOCATION_BY_SLUG';
